import React, { useRef, useState } from "react";
import { Button, Textarea } from "@headlessui/react";
import { HandThumbUpIcon } from "@heroicons/react/24/outline";
import { deepEqual } from "fast-equals";
import { handleEnterKey } from "../../../app/keyboardUtils";
import { AlertLevel } from "../../../components/AlertMessage";
import ButtonWithConfirm from "../../../components/ButtonWithConfirm";
import ModalFrame from "../../../components/ModalFrame";
import {
	LlmFeedbackCommentRequestModel,
	LlmFeedbackCommentViewModel,
	useDeleteLlmFeedbackCommentMutation,
	useLazyGetLlmFeedbackCommentQuery,
	useSaveLlmFeedbackCommentMutation,
} from "../../llmFeedback/llmFeedbackApiSlice";

export interface Props {
	className: string;
	heading: string;
	traitId: number;
	llmFeedbackId?: number;
	llmFeedback: string[];
	showCommentButton: boolean;
}

export function TraitLlmFeedback(props: Props) {
	const buttonRef = useRef<HTMLInputElement>(null);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [commentModel, setCommentModel] = useState<LlmFeedbackCommentViewModel>();

	const [getCommentModel] = useLazyGetLlmFeedbackCommentQuery();

	const handleOpenCommentDialog = () => {
		const args: LlmFeedbackCommentRequestModel = {
			llmFeedbackId: props.llmFeedbackId!,
			scoreCategoryId: props.traitId,
		};
		buttonRef.current!.disabled = true;
		getCommentModel(args)
			.unwrap()
			.then((data) => {
				setCommentModel(data);
				setIsDialogOpen(true);
			});
	};

	const handleConfirmFeedback = () => {
		setIsDialogOpen(false);
		buttonRef.current!.disabled = false;
	};

	const handleCancelFeedback = () => {
		setIsDialogOpen(false);
		buttonRef.current!.disabled = false;
	};

	if (!props.llmFeedback || props.llmFeedback.length === 0) return null;

	return (
		<section className={`bg-gray-50 border border-gray-200 rounded-lg p-4 ${props.className}`}>
			<h3 className="pt-0">{props.heading}</h3>
			{props.llmFeedback.map((feedback, index) => {
				return (
					<div key={index}>
						<p>{feedback}</p>
						{props.traitId && props.llmFeedbackId && props.showCommentButton && (
							<div className="flex justify-end mt-2 print:!hidden">
								<Button
									className="btn btn-neutral"
									onClick={handleOpenCommentDialog}
									ref={buttonRef}>
									<HandThumbUpIcon className="h-4 inline mr-2 print:!hidden" />
									Was this advice helpful?
								</Button>
							</div>
						)}
					</div>
				);
			})}
			{isDialogOpen && commentModel && (
				<ModalFrame
					open={isDialogOpen}
					onClose={() => setIsDialogOpen(false)}
					title="Writing Advisor Feedback">
					<WritingAdvisorFeedbackDialog
						model={commentModel}
						onCancel={handleCancelFeedback}
						onConfirm={handleConfirmFeedback}
					/>
				</ModalFrame>
			)}
		</section>
	);
}

export interface WritingAdvisorFeedbackProps {
	onWasHelpfulChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onCommentChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

export interface WritingAdvisorFeedbackDialogProps {
	model: LlmFeedbackCommentViewModel;
	onConfirm: () => void;
	onCancel: () => void;
}

export function WritingAdvisorFeedbackDialog(props: WritingAdvisorFeedbackDialogProps) {
	const [commentModel, setCommentModel] = useState<LlmFeedbackCommentViewModel>(props.model);
	const [isConfirmDisabled, setIsConfirmDisabled] = useState(true);

	const [saveCommentModel] = useSaveLlmFeedbackCommentMutation();
	const [deleteCommentModel] = useDeleteLlmFeedbackCommentMutation();

	const cancelButtonLabel = commentModel.isReadonly ? "Close" : "Cancel";

	const handleWasHelpful = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!commentModel) return;
		const updatedCommentModel = {
			...commentModel,
			wasHelpful: event.target.value == "true",
		};
		setCommentModel(updatedCommentModel);
		setIsConfirmDisabled(deepEqual(props.model, updatedCommentModel));
	};

	const handleComment = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		if (!commentModel) return;
		const updatedCommentModel = {
			...commentModel,
			comment: event.target.value,
		};
		setCommentModel(updatedCommentModel);
		setIsConfirmDisabled(deepEqual(props.model, updatedCommentModel));
	};

	function handleDelete() {
		if (!commentModel) return;
		deleteCommentModel(commentModel);
		props.onCancel();
	}
	function handleConfirm() {
		if (!commentModel) return;
		saveCommentModel(commentModel);
		props.onConfirm();
	}

	function handleCancel() {
		props.onCancel();
	}

	// ---[ for testing until integrated with teacher role ]---
	// const toggleReadonly = () => {
	// 	const updatedCommentModel = {
	// 		...commentModel,
	// 		isReadonly: !commentModel.isReadonly,
	// 	};
	// 	setCommentModel(updatedCommentModel);
	// };

	return (
		<div>
			<p className="mt-2">
				Your input will help us improve Writing Advisor!
				{/* Your input will help us improve Writing Advisor! (
				<span onClick={toggleReadonly} className="text-red-800 underline cursor-pointer">
					toggle readonly {commentModel.isReadonly ? "off" : "on"}
				</span>
				) */}
			</p>

			<div className="mt-2">
				<label htmlFor="radio-group-was-helpful" className="font-semibold text-sm">
					Was this advice helpful?
				</label>
				<div className="flex items-center">
					<input
						id="radio-yes"
						type="radio"
						value="true"
						name="radio-group-was-helpful"
						className="w-4 h-4"
						readOnly={commentModel.isReadonly}
						disabled={commentModel.isReadonly}
						checked={commentModel.wasHelpful}
						onChange={handleWasHelpful}
					/>
					<label htmlFor="radio-yes" className="ms-2 me-8 text-sm">
						Yes
					</label>

					<input
						id="radio-no"
						type="radio"
						value="false"
						name="radio-group-was-helpful"
						className="w-4 h-4"
						readOnly={commentModel.isReadonly}
						disabled={commentModel.isReadonly}
						checked={!commentModel.wasHelpful}
						onChange={handleWasHelpful}
					/>
					<label htmlFor="radio-no" className="ms-2 text-sm">
						No
					</label>
				</div>
			</div>

			<div className="mt-2">
				<label htmlFor="comment" className="font-semibold text-sm">
					Comment
				</label>
				<Textarea
					name="comment"
					rows={3}
					className="block w-full"
					readOnly={commentModel.isReadonly}
					value={commentModel.comment}
					onChange={handleComment}
				/>
			</div>

			<div className="flex justify-between mt-4">
				<div className="flex justify-start gap-2">
					{!commentModel.isReadonly && props.model.comment && (
						<ButtonWithConfirm
							id="delete-feedback-button"
							className="btn btn-neutral"
							message="Are you sure you want to delete your feedback?"
							type={AlertLevel.Warning}
							confirmLabel="Yes"
							cancelLabel="Cancel"
							onConfirm={handleDelete}>
							Delete
						</ButtonWithConfirm>
					)}
					{commentModel.isReadonly && (
						<p>Feedback from {commentModel.authorRole.toLowerCase()}.</p>
					)}
				</div>
				<div className="flex justify-end gap-2">
					<button
						type="button"
						id="cancel-button"
						onClick={handleCancel}
						onKeyDown={(e) => handleEnterKey(e, handleCancel)}
						className="btn btn-neutral">
						{cancelButtonLabel}
					</button>
					{!commentModel.isReadonly && (
						<button
							type="button"
							id="confirm-button"
							disabled={isConfirmDisabled}
							data-autofocus
							onClick={handleConfirm}
							onKeyDown={(e) => handleEnterKey(e, handleConfirm)}
							className="btn btn-primary">
							Submit
						</button>
					)}
				</div>
			</div>
		</div>
	);
}
